/**
 * Generates user initials for avatar display
 * Priority:
 * 1. First and Last Name: "Martin Hwang" -> "MH"
 * 2. Single Name: "Sydney" -> "SY" (first two letters)
 * 3. Email: "developer.martinhwang@gmail.com" -> "D" (first letter of local part)
 * 4. Fallback: "?"
 */
export const getUserInitials = (firstName?: string, lastName?: string, email?: string | null): string => {
  // Case 1: Both first and last name available
  if (firstName && lastName) {
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  }

  // Case 2: Only first name available (or last name only)
  const singleName = firstName || lastName;
  if (singleName) {
    // If name has only one character, return it twice
    if (singleName.length === 1) {
      return singleName.toUpperCase();
    }
    // Return first two letters
    return singleName.substring(0, 2).toUpperCase();
  }

  // Case 3: Email fallback
  if (email) {
    const localPart = email.split('@')[0];
    if (localPart && localPart.length > 0) {
      return localPart.charAt(0).toUpperCase();
    }
  }

  // Case 4: Ultimate fallback
  return '?';
};

/**
 * Gets a consistent color for a user based on their name or email
 * This ensures the same user always gets the same avatar color
 */
export const getAvatarColorFromString = (str: string): string => {
  const colors = [
    '#1976d2', // blue
    '#d32f2f', // red
    '#7b1fa2', // purple
    '#388e3c', // green
    '#f57c00', // orange
    '#0097a7', // cyan
    '#c2185b', // pink
    '#5d4037', // brown
    '#455a64', // blue grey
    '#00796b', // teal
  ];

  // Simple hash function to get consistent color
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

/**
 * Available avatar colors for user selection
 */
export const AVATAR_COLORS = [
  '#1976d2', // blue
  '#d32f2f', // red
  '#7b1fa2', // purple
  '#388e3c', // green
  '#f57c00', // orange
  '#0097a7', // cyan
  '#c2185b', // pink
  '#5d4037', // brown
  '#455a64', // blue grey
  '#00796b', // teal
  '#e91e63', // bright pink
  '#9c27b0', // deep purple
  '#3f51b5', // indigo
  '#2196f3', // light blue
  '#00bcd4', // dark cyan
];

/**
 * Gets the avatar background color
 * Prioritizes user's selected color, then falls back to generated color
 */
export const getAvatarColor = (
  userSelectedColor?: string,
  fallbackString?: string | null,
  defaultColor?: string
): string => {
  // Priority 1: User's chosen color
  if (userSelectedColor) {
    return userSelectedColor;
  }

  // Priority 2: Generate color from string
  if (fallbackString) {
    return getAvatarColorFromString(fallbackString);
  }

  // Priority 3: Default color
  return defaultColor || '#1976d2';
};
